import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import PhotoStrip from '../../components/review/PhotoStrip';
import ConfirmSheet from '../../components/ui/ConfirmSheet';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  MyReview,
  REVIEW_EDIT_DAYS,
  reviewMaxPhotos,
  ReviewPhoto,
  visiblePhotos,
  labelForTag,
} from '../../data/reviews';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * What you said about the work - and the controls to change it.
 *
 * ---------------------------------------------------------------------------
 * TWO TABS, ONE OBJECT
 * ---------------------------------------------------------------------------
 * A review and a bare rating are the same record with and without words. They
 * are split across two tabs because they read completely differently - a star
 * with no comment has nothing to put in a review card, and padding one out
 * with an empty quote looks like a review that failed to load - but there is
 * one review per booking underneath both.
 *
 * ---------------------------------------------------------------------------
 * TAKING A PHOTO DOWN IS SELF-SERVICE
 * ---------------------------------------------------------------------------
 * Somebody who regrets posting a picture of their own living room should not
 * have to explain themselves to support to get it off the internet. The ✕ on
 * the thumbnail hides it immediately, behind one confirmation, with no reason
 * asked and no queue. That is the whole of US-10.10: it is regret, not a
 * complaint, and the two need very different flows.
 *
 * The review itself survives - its rating still counts - which is why the
 * photo is hidden rather than the row deleted.
 */

type Tab = 'reviews' | 'ratings';

/** Filled up to `stars`, hollow after - always five, so the row scans. */
function Stars({ stars, size = 14 }: { stars: number; size?: number }) {
  return (
    <View
      style={styles.stars}
      accessible
      accessibilityLabel={`${stars} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map(i => (
        <Icon
          key={i}
          name={i <= stars ? 'starFilled' : 'star'}
          size={size}
          color={i <= stars ? colors.rating : colors.borderStrong}
        />
      ))}
    </View>
  );
}

type Props = {
  onBack: () => void;
  /** Every review left, keyed by booking. */
  reviews: Record<string, MyReview>;
  /** Opens the composer on an existing review. */
  onEdit?: (bookingId: string) => void;
  /** Hides one photograph. Confirmed here, applied by the caller. */
  onHidePhoto?: (bookingId: string, photoId: string) => void;
};

function RatingsScreen({ onBack, reviews, onEdit, onHidePhoto }: Props) {
  const resp = useResponsive();
  const [tab, setTab] = useState<Tab>('reviews');

  /** The photo a confirmation is currently open about. */
  const [takedown, setTakedown] = useState<{
    review: MyReview;
    photo: ReviewPhoto;
  } | null>(null);

  const all = Object.values(reviews);
  /* Words or pictures make it a review; stars alone make it a rating. */
  const written = all.filter(
    review => review.comment.length > 0 || visiblePhotos(review).length > 0,
  );
  const shown = tab === 'reviews' ? written : all;

  return (
    <View style={styles.root}>
      <PageHeader title="My Ratings & Reviews" onBack={onBack} />

      <View style={styles.tabs}>
        {(
          [
            { key: 'reviews', label: 'Reviews' },
            { key: 'ratings', label: 'Ratings Given' },
          ] as Array<{ key: Tab; label: string }>
        ).map(item => {
          const on = item.key === tab;
          return (
            <Pressable
              key={item.key}
              accessibilityRole="button"
              accessibilityState={{ selected: on }}
              accessibilityLabel={item.label}
              onPress={() => setTab(item.key)}
              style={[styles.tab, on ? styles.tabOn : null]}
            >
              <Text
                variant="labelSm"
                color={on ? colors.onPrimary : colors.inkBody}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off. Flexed, it takes the space left under the header and scrolls
        inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {shown.length === 0 ? (
          <EmptyState
            icon="star"
            title="Nothing yet"
            message="Rate a completed booking and it appears here, where you can edit it."
          />
        ) : (
          shown.map(review => (
            <View key={review.bookingId} style={styles.card}>
              <View style={styles.mark}>
                <Icon name={review.icon} size={19} color={colors.primary} />
              </View>

              <View style={styles.body}>
                <View style={styles.head}>
                  <Text
                    variant="subtitle"
                    style={styles.grow}
                    numberOfLines={1}
                  >
                    {review.service}
                  </Text>
                  <Text variant="caption" color={colors.inkMuted}>
                    {review.when}
                  </Text>
                </View>

                <Stars stars={review.stars} />

                {review.comment ? (
                  <Text variant="bodySm" color={colors.inkBody}>
                    {review.comment}
                  </Text>
                ) : null}

                {review.tags.length ? (
                  <View style={styles.tagRow}>
                    {/* Stored as the server's keys - `clean_work`, not "Clean
                        work" - and worded for reading here. Storing them
                        pre-worded is exactly what let the label and the counted
                        value drift apart. */}
                    {review.tags.map(tag => (
                      <View key={tag} style={styles.tagChip}>
                        <Text variant="caption" color={colors.inkBody}>
                          {labelForTag(tag)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {/* Read-only apart from the ✕: there is no adding a photo from
                    the list, because adding one is editing the review. */}
                {visiblePhotos(review).length ? (
                  <PhotoStrip
                    photos={review.photos}
                    max={reviewMaxPhotos()}
                    onHide={
                      onHidePhoto
                        ? photo => setTakedown({ review, photo })
                        : undefined
                    }
                  />
                ) : null}

                <View style={styles.actions}>
                  {review.edited ? (
                    <Text variant="caption" color={colors.inkSubtle}>
                      Edited
                    </Text>
                  ) : null}
                  {onEdit ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Edit your review of ${review.service}`}
                      onPress={() => onEdit(review.bookingId)}
                      style={({ pressed }) => [
                        styles.editButton,
                        pressed ? styles.pressed : null,
                      ]}
                    >
                      <Text variant="labelSm" color={colors.primary}>
                        Edit
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
            </View>
          ))
        )}

        <View style={styles.footnote}>
          <Icon name="shieldCheck" size={15} color={colors.primary} />
          <Text variant="caption" color={colors.inkMuted} style={styles.grow}>
            You can edit or delete reviews within {REVIEW_EDIT_DAYS} days of
            completion. Photos can be taken down at any time.
          </Text>
        </View>
      </ScrollView>

      {/*
        One confirmation, no reason asked.

        The copy says what actually happens - it comes off the professional's
        profile too - because "Remove photo" alone leaves people wondering
        whether it is only hidden from them.
      */}
      <ConfirmSheet
        visible={takedown !== null}
        icon="image"
        title="Remove this photo?"
        body="It comes off your review and off the professional's profile straight away. Your rating and words stay."
        confirmLabel="Remove photo"
        cancelLabel="Keep it"
        onConfirm={() => {
          if (takedown) {
            onHidePhoto?.(takedown.review.bookingId, takedown.photo.id);
          }
          setTakedown(null);
        }}
        onClose={() => setTakedown(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  tabs: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
  },
  tab: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  tabOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  body: {
    flex: 1,
    gap: 4,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  grow: {
    flex: 1,
  },
  stars: {
    flexDirection: 'row',
    gap: 2,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: 2,
  },
  tagChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  editButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  footnote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.md,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default RatingsScreen;
